import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import TextView from "./TextView";
import { BuildContext } from "../classes/BuildContext";

type _BooleanToggleViewOnChanged = (value: boolean) => void;

export interface BooleanToggleViewProps extends BaseUIProps {
  key?: string;
  activeColor?: ui.Color;
  inActiveColor?: ui.Color;
  activeText?: string;
  inActiveText?: string;
  activeTextColor?: ui.Color;
  inActiveTextColor?: ui.Color;
  value?: boolean;
  fontSize?: number;
  labelFontSize?: number;
  labelColor?: ui.Color;
  label?: string;
  onChanged?: _BooleanToggleViewOnChanged;
}

class _BooleanToggleViewState extends ObservableComponent<BooleanToggleViewProps> {
  static defaultProps = {
    activeColor: null,
    inActiveColor: null,
    activeText: "Active",
    inActiveText: "In Active",
    activeTextColor: null,
    inActiveTextColor: null,
    value: false,
    fontSize: 12.0,
    labelFontSize: 12.0,
    labelColor: null,
    label: "",
    onChanged: null,
  };
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: BooleanToggleViewProps) {
    super(props);

    this.initState();
  }
  public get activeColor(): ui.Color {
    return this.props.activeColor;
  }
  public get inActiveColor(): ui.Color {
    return this.props.inActiveColor;
  }
  public get activeText(): string {
    return this.props.activeText;
  }
  public get inActiveText(): string {
    return this.props.inActiveText;
  }
  public get activeTextColor(): ui.Color {
    return this.props.activeTextColor;
  }
  public get inActiveTextColor(): ui.Color {
    return this.props.inActiveTextColor;
  }
  public get value(): boolean {
    return this.props.value;
  }
  public get fontSize(): number {
    return this.props.fontSize;
  }
  public get labelFontSize(): number {
    return this.props.labelFontSize;
  }
  public get labelColor(): ui.Color {
    return this.props.labelColor;
  }
  public get label(): string {
    return this.props.label;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      [
        "activeColor",
        "activeText",
        "activeTextColor",
        "fontSize",
        "inActiveColor",
        "inActiveText",
        "inActiveTextColor",
        "label",
        "labelColor",
        "labelFontSize",
        "value",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: BooleanToggleViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.activeColor !== this.props.activeColor) {
      this.fire("activeColor", this);
    }

    if (prevProps.inActiveColor !== this.props.inActiveColor) {
      this.fire("inActiveColor", this);
    }

    if (prevProps.activeText !== this.props.activeText) {
      this.fire("activeText", this);
    }

    if (prevProps.inActiveText !== this.props.inActiveText) {
      this.fire("inActiveText", this);
    }

    if (prevProps.activeTextColor !== this.props.activeTextColor) {
      this.fire("activeTextColor", this);
    }

    if (prevProps.inActiveTextColor !== this.props.inActiveTextColor) {
      this.fire("inActiveTextColor", this);
    }

    if (prevProps.value !== this.props.value) {
      this.fire("value", this);
    }

    if (prevProps.fontSize !== this.props.fontSize) {
      this.fire("fontSize", this);
    }

    if (prevProps.labelFontSize !== this.props.labelFontSize) {
      this.fire("labelFontSize", this);
    }

    if (prevProps.labelColor !== this.props.labelColor) {
      this.fire("labelColor", this);
    }

    if (prevProps.label !== this.props.label) {
      this.fire("label", this);
    }
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      mainAxisAlignment: ui.MainAxisAlignment.start,
      crossAxisAlignment: ui.CrossAxisAlignment.start,
      children: [
        this.label !== null && this.label.isNotEmpty
          ? TextView({
              data: this.label,
              states: ui.joinStates(
                { "data-visibility": this.label === null },
                {}
              ),
              style: new ui.TextStyle({
                fontWeight: ui.FontWeight.w500,
                fontSize: this.labelFontSize,
                color:
                  this.labelColor !== null
                    ? this.labelColor
                    : ui.HexColor.fromHexInt(0xff000000),
              }),
              className: "x14d",
            })
          : [],
        this.value
          ? ui.Container({
              decoration: new ui.BoxDecoration({
                border: ui.Border.all({ color: cStyle.c5, width: 1.0 }),
                borderRadius: ui.BorderRadius.circular(30.0),
                color:
                  this.activeColor !== null
                    ? this.activeColor
                    : ui.HexColor.fromHexInt(0xff0e9b6c),
              }),
              child: ui.Wrap({
                crossAxisAlignment: ui.WrapCrossAlignment.center,
                children: [
                  TextView({
                    data: this.activeText,
                    style: new ui.TextStyle({
                      color:
                        this.activeTextColor !== null
                          ? this.activeTextColor
                          : ui.HexColor.fromHexInt(0xffffffff),
                      fontSize: this.fontSize,
                    }),
                    className: "x3f",
                    key: "0",
                  }),
                  ui.Container({ className: "xd89 hc vc", key: "1" }),
                ],
              }),
              className: "x061",
            })
          : ui.Container({
              decoration: new ui.BoxDecoration({
                border: ui.Border.all({ color: cStyle.c5, width: 1.0 }),
                borderRadius: ui.BorderRadius.circular(30.0),
                color:
                  this.inActiveColor !== null
                    ? this.inActiveColor
                    : ui.HexColor.fromHexInt(0xffe30000),
              }),
              child: ui.Wrap({
                crossAxisAlignment: ui.WrapCrossAlignment.center,
                children: [
                  ui.Container({ className: "x965 hc vc", key: "0" }),
                  TextView({
                    data: this.inActiveText,
                    style: new ui.TextStyle({
                      color:
                        this.inActiveTextColor !== null
                          ? this.inActiveTextColor
                          : ui.HexColor.fromHexInt(0xffffffff),
                      fontSize: this.fontSize,
                    }),
                    className: "x49",
                    key: "1",
                  }),
                ],
              }),
              className: "x842",
            }),
      ],
      onTap: (e) => {
        e.stopPropagation();

        this.onChange();
      },
      className: ui.join(this.props.className, "BooleanToggleView"),
      ...copyBaseUIProps(this.props),
    });
  }
  public onChange = (): void => {
    if (this.onChanged !== null) {
      this.onChanged(!this.value);
    }
  };
  public get onChanged(): _BooleanToggleViewOnChanged {
    return this.props.onChanged;
  }
}
export default function BooleanToggleView(props: BooleanToggleViewProps) {
  return React.createElement(_BooleanToggleViewState, {
    ..._BooleanToggleViewState.defaultProps,
    ...props,
  });
}
